import { SelectOptionGroups } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import type { AllFeatureIds } from 'generated/featureIds.generated'

import { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'

import { ExportFormat } from 'modules/payroll_exporter/lib/format'
import type LegalEntitySettings from 'modules/payroll_exporter/store/models/LegalEntitySettings'

interface ExportOption {
  format: ExportFormat
  settingName?: AllFeatureIds
  label: string
  order?: number
}

const FORMATS_WITH_TIME_OFF: ReadonlySet<ExportFormat> = new Set([
  ExportFormat.DELTA,
  ExportFormat.PAIERH_LEAVES,
  ExportFormat.YEAP_PAIERH_LEAVES,
  ExportFormat.SILAE_LEAVES,
  ExportFormat.SILAE_LEAVE_DELTAS,
  ExportFormat.ZUCCHETTI_LEAVES,
  ExportFormat.ZUCCHETTI_TIME,
  ExportFormat.GISPAGHE_TIME,
  ExportFormat.TIMEOFF,
])

const FORMATS_WITH_TIME_TRACKING: ReadonlySet<ExportFormat> = new Set([
  ExportFormat.ZUCCHETTI_TIME,
  ExportFormat.GISPAGHE_TIME,
])

const AVAILABLE_EXPORT_OPTIONS = (): ExportOption[] => [
  {
    format: ExportFormat.SUPPLEMENTS,
    label: i18n.t('payroll_bundle.export.new_formats.supplements'),
  },
  {
    format: ExportFormat.TIMEOFF,
    label: i18n.t('payroll_bundle.export.new_formats.timeoff'),
  },
  {
    format: ExportFormat.BASIC,
    label: i18n.t('payroll_bundle.export.new_formats.basic'),
  },
  {
    format: ExportFormat.EXTENDED,
    label: i18n.t('payroll_bundle.export.new_formats.extended'),
  },
  {
    format: ExportFormat.DELTA,
    label: i18n.t('payroll_bundle.export.new_formats.delta'),
  },
  {
    format: ExportFormat.PAYSLIP_CONTRACT_CHANGES,
    settingName: 'dev_payroll_export_payslip_contract_changes',
    label: i18n.t('payroll_bundle.export.new_formats.payslip_contract_changes'),
  },
  {
    format: ExportFormat.A3NOM_SUPPLEMENTS,
    settingName: 'payroll_integrations_a3nom',
    label: i18n.t('payroll_bundle.export.new_formats.a3nom_supplements'),
  },
  {
    format: ExportFormat.A3NOM_EMPLOYEES,
    settingName: 'payroll_integrations_a3nom',
    label: i18n.t('payroll_bundle.export.new_formats.a3nom_employees'),
  },
  {
    format: ExportFormat.DATEV_EMPLOYEES,
    settingName: 'payroll_integrations_datev',
    label: i18n.t('payroll_bundle.export.new_formats.datev_employees'),
  },
  {
    format: ExportFormat.PAIERH_LEAVES,
    settingName: 'payroll_integrations_paierh',
    label: i18n.t('payroll_bundle.export.new_formats.paierh_leaves'),
  },
  {
    format: ExportFormat.PAIERH_UPDATED_EMPLOYEES,
    settingName: 'payroll_integrations_paierh',
    label: i18n.t('payroll_bundle.export.new_formats.paierh_updated_employees'),
  },
  {
    format: ExportFormat.PAIERH_SUPPLEMENTS,
    settingName: 'payroll_integrations_paierh',
    label: i18n.t('payroll_bundle.export.new_formats.paierh_supplements'),
  },
  {
    format: ExportFormat.YEAP_PAIERH_LEAVES,
    settingName: 'payroll_integrations_yeap_paierh',
    label: i18n.t('payroll_bundle.export.new_formats.yeap_paierh_leaves'),
  },
  {
    format: ExportFormat.YEAP_PAIERH_UPDATED_EMPLOYEES,
    settingName: 'payroll_integrations_yeap_paierh',
    label: i18n.t('payroll_bundle.export.new_formats.yeap_paierh_updated_employees'),
  },
  {
    format: ExportFormat.YEAP_PAIERH_SUPPLEMENTS,
    settingName: 'payroll_integrations_yeap_paierh',
    label: i18n.t('payroll_bundle.export.new_formats.yeap_paierh_supplements'),
  },
  {
    format: ExportFormat.SILAE_LEAVES,
    settingName: 'payroll_integrations_silae',
    label: i18n.t('payroll_bundle.export.new_formats.silae_leaves'),
  },
  {
    format: ExportFormat.SILAE_SUPPLEMENTS,
    settingName: 'payroll_integrations_silae',
    label: i18n.t('payroll_bundle.export.new_formats.silae_supplements'),
  },
  {
    format: ExportFormat.SILAE_LEAVE_DELTAS,
    settingName: 'payroll_integrations_silae',
    label: i18n.t('payroll_bundle.export.new_formats.silae_leave_deltas'),
  },
  {
    format: ExportFormat.ZUCCHETTI_LEAVES,
    settingName: 'payroll_integrations_zucchetti',
    label: i18n.t('payroll_bundle.export.new_formats.zucchetti_leaves'),
  },
  {
    format: ExportFormat.ZUCCHETTI_TIME,
    label: i18n.t('payroll_bundle.export.new_formats.zucchetti_time'),
    settingName: 'payroll_integrations_zucchetti',
  },
  {
    format: ExportFormat.GISPAGHE_TIME,
    label: i18n.t('payroll_bundle.export.new_formats.gispaghe_time'),
    settingName: 'payroll_integrations_gispaghe',
  },
  {
    format: ExportFormat.EVENTS_NEW_HIRES,
    label: i18n.t('payroll_bundle.export.new_formats.events_new_hires'),
  },
  {
    format: ExportFormat.CONTRACT_CHANGES,
    label: i18n.t('payroll_bundle.export.new_formats.contract_changes'),
  },
  {
    format: ExportFormat.EVENTS_PERSONAL_CHANGES,
    label: i18n.t('payroll_bundle.export.new_formats.personal_changes'),
  },
  {
    format: ExportFormat.EVENTS_TERMINATIONS,
    label: i18n.t('payroll_bundle.export.new_formats.terminations'),
  },
]

type ExportGroupKey = 'payroll_data' | 'global_formats'

type GroupedExports = {
  [key in string]: { label: string; value: string }[]
}

const EXPORTABLES_DEFINED_BY_GROUPS = (exportFormat: ExportFormat): ExportGroupKey | null => {
  switch (exportFormat) {
    case ExportFormat.BASIC:
    case ExportFormat.EXTENDED:
      return 'global_formats'
    default:
      return 'payroll_data'
  }
}

const keyMappings = (): Record<ExportGroupKey, string> => ({
  payroll_data: i18n.t('payroll_bundle.export.export_payroll_group'),
  global_formats: i18n.t('payroll_bundle.export.export_globals_groups'),
})

interface useExportableOptionsByModulesProps {
  legalEntitySettings?: LegalEntitySettings
  groupExports?: boolean
  sortExports?: boolean
  moduleAvailabilities: ModuleAvailabilityType[]
}

export const useExportableOptionsByModules = (
  props: useExportableOptionsByModulesProps
): SelectOptionGroups => {
  const { legalEntitySettings, groupExports, sortExports, moduleAvailabilities } = props

  const hasTimeoffModule = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.timeoff
  )

  const hasTimeTrackingModule = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.time_tracking
  )

  const hasCompensationsModule = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.compensations
  )

  const keyMappingsLabels = keyMappings()

  // sort by order, if only one has order prioritize order and if no one has order then by label
  function compareExportOptions(a: ExportOption, b: ExportOption) {
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order
    } else if (a.order !== undefined) {
      return -1
    } else if (b.order !== undefined) {
      return 1
    } else {
      return a.label.localeCompare(b.label)
    }
  }

  const availableExports = AVAILABLE_EXPORT_OPTIONS().filter((exportOption) =>
    isExportable(
      hasTimeoffModule,
      hasTimeTrackingModule,
      hasCompensationsModule,
      exportOption,
      legalEntitySettings
    )
  )

  if (sortExports) availableExports.sort(compareExportOptions)

  if (groupExports) {
    const groupedExports: GroupedExports = {
      [keyMappingsLabels.payroll_data]: [],
      [keyMappingsLabels.global_formats]: [],
    }

    availableExports.forEach((exportOption) => {
      const groupKey = EXPORTABLES_DEFINED_BY_GROUPS(exportOption.format)

      if (groupKey) {
        const keyLabel = keyMappingsLabels[groupKey]

        groupedExports[keyLabel]?.push({
          label: exportOption.label,
          value: exportOption.format,
        })
      }
    })

    // Remove empty groups, not really needed now but if we add more groups in the future will be useful
    const filteredGroupedExports: GroupedExports = Object.fromEntries(
      Object.entries(groupedExports).filter(([_key, value]) => value.length > 0)
    )

    return filteredGroupedExports
  }

  return availableExports.map(({ label, format }) => {
    return {
      label,
      value: format,
    }
  })
}

const isExportable = (
  hasTimeoffModule: boolean,
  hasTimeTrackingModule: boolean,
  hasCompensationsModule: boolean,
  exportOption: ExportOption,
  legalEntitySettings?: LegalEntitySettings
): boolean => {
  const settingName = exportOption.settingName
  const format = exportOption.format

  if (settingName && !legalEntitySettings?.hasFeature(settingName)) return false

  if (legalEntitySettings?.hasFeature('dev_packaging_paywalls')) {
    if (FORMATS_WITH_TIME_OFF.has(format) && FORMATS_WITH_TIME_TRACKING.has(format)) {
      return hasTimeoffModule && hasTimeTrackingModule
    }
    if (FORMATS_WITH_TIME_OFF.has(format)) return hasTimeoffModule
    if (FORMATS_WITH_TIME_TRACKING.has(format)) return hasTimeTrackingModule
  }

  if (
    hasCompensationsModule &&
    [
      ExportFormat.SUPPLEMENTS,
      ExportFormat.A3NOM_SUPPLEMENTS,
      ExportFormat.SILAE_SUPPLEMENTS,
      ExportFormat.PAIERH_SUPPLEMENTS,
      ExportFormat.YEAP_PAIERH_SUPPLEMENTS,
    ].includes(format)
  )
    return false

  return true
}
